import moment from 'moment'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type { AllowanceStatFragment } from 'generated/resources/graphql'

// Raw cycle data from the GraphQL JSON string
interface RawCycle {
  id: string
  start_at: string
  end_at: string
  regular_start_at: string
  regular_end_at: string
}

// CarryOver type matching the structure expected by TotalTooltip/Carryovers
export interface CarryOver {
  accumulated: number
  expire_in_months: number
  expired: number
  from_cycle_ending_on: string
}

// Cycle class that matches the MobX model's Cycle interface
export class Cycle {
  id: string
  begin: moment.Moment
  end: moment.Moment
  regularBegin: moment.Moment
  regularEnd: moment.Moment

  constructor({
    id,
    begin,
    end,
    regularBegin,
    regularEnd,
  }: {
    id: string
    begin: moment.Moment
    end: moment.Moment
    regularBegin: moment.Moment
    regularEnd: moment.Moment
  }) {
    this.id = id
    this.begin = begin.clone().startOf('day')
    this.end = end.clone().endOf('day')
    this.regularBegin = regularBegin.clone().startOf('day')
    this.regularEnd = regularEnd.clone().endOf('day')
  }

  covers(date: moment.Moment): boolean {
    return date.isSameOrAfter(this.begin) && date.isSameOrBefore(this.end)
  }

  regularRangeCovers(date: moment.Moment): boolean {
    return date.isSameOrAfter(this.regularBegin) && date.isSameOrBefore(this.regularEnd)
  }

  /**
   * NOTE: We intentionally keep this false in the GraphQL counter flow.
   *
   * The legacy behavior depended on a local feature flag (`hasFeature`) that cannot be used here
   * due to semgrep restrictions. NewStatsCounter follows the non-strict range semantics by design,
   * so incidenceDateCheck always uses regularRangeCovers.
   */
  get strictCycleCheck(): boolean {
    return false
  }

  /**
   * Checks if a date falls within the cycle regular range.
   */
  incidenceDateCheck(date: moment.Moment): boolean {
    return this.regularRangeCovers(date)
  }

  get startEndSameYear(): boolean {
    return this.begin.year() === this.end.year()
  }

  get yearLabel(): string {
    return this.startEndSameYear
      ? `${this.begin.year()}`
      : `${this.begin.year()}/${this.end.year()}`
  }

  get rangeLabel(): string {
    const cycleStartFormat = this.startEndSameYear ? 'ui_dm_long' : 'ui_dmy_long'
    const cycleEndFormat = 'ui_dmy_long'

    return i18n.t('time_off.cycle', {
      begin: i18nDateToString(this.begin, cycleStartFormat),
      end: i18nDateToString(this.end, cycleEndFormat),
    })
  }
}

// Attributes interface matching the snake_case format used by child components
export interface AllowanceStatAttributes {
  id: string
  allowance_id: string
  available_days: number
  used_days: number
  prorated_allowance_days: number
  incidences: number
  accumulated_carry_over: number
  used_carry_over: number
  carry_overs: CarryOver[]
  policy_allowance: number
  total_accrued_units: number
  total_in_decimal: number | null
  max_balance_cap: number | null
  outstanding_units: number
  used_units_until_reference_date: number
  year: number
  cycles: RawCycle[]
  effective_accrued: number | null
  advance: number | null
  taken_from_carry_over: number | null
  taken_from_incidences: number | null
  taken_from_accrued: number | null
  taken_as_advance: number | null
  carry_over_remaining: number | null
  incidences_remaining: number | null
  accrued_remaining: number | null
  total: number
}

// AllowanceStat-like interface that child components expect
export interface ParsedAllowanceStat {
  attributes: AllowanceStatAttributes
  cycles: Cycle[]
  firstCycle: Cycle | undefined
  lastCycle: Cycle | undefined
  cycleByDate: (date: moment.Moment) => Cycle | undefined
  nextCycle: (cycle: Cycle) => Cycle | undefined
  prevCycle: (cycle: Cycle) => Cycle | undefined
  toJS: () => AllowanceStatAttributes
}

/**
 * Parses the cycles JSON string and returns both Cycle objects and raw cycle data.
 * This avoids double-parsing the JSON string.
 */
function parseCyclesWithRaw(cyclesJson: string): { cycles: Cycle[]; rawCycles: RawCycle[] } {
  let rawCycles: RawCycle[] = []

  try {
    rawCycles = JSON.parse(cyclesJson)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse allowance cycles JSON. Falling back to empty cycles array.', {
      error,
      cyclesJson,
    })
    rawCycles = []
  }

  if (!Array.isArray(rawCycles)) {
    rawCycles = []
  }

  const cycles = rawCycles.map(
    (cycle) =>
      new Cycle({
        id: cycle.id,
        begin: stringToDate(cycle.start_at, 'backend'),
        end: stringToDate(cycle.end_at, 'backend'),
        regularBegin: stringToDate(cycle.regular_start_at, 'backend'),
        regularEnd: stringToDate(cycle.regular_end_at, 'backend'),
      }),
  )

  return { cycles, rawCycles }
}

/**
 * Type guard to check if an object is a valid CarryOver
 */
function isCarryOver(obj: unknown): obj is CarryOver {
  if (typeof obj !== 'object' || obj === null) return false

  const hasNumberProp = (key: string): boolean => {
    return typeof Reflect.get(obj, key) === 'number'
  }

  const hasStringProp = (key: string): boolean => {
    return typeof Reflect.get(obj, key) === 'string'
  }

  return (
    hasNumberProp('accumulated') &&
    hasNumberProp('expire_in_months') &&
    hasNumberProp('expired') &&
    hasStringProp('from_cycle_ending_on')
  )
}

/**
 * Parses the carryOvers JSON strings from GraphQL into CarryOver objects
 */
function parseCarryOvers(carryOversJsonArray: string[]): CarryOver[] {
  return carryOversJsonArray
    .map((jsonStr) => {
      try {
        const parsed: unknown = JSON.parse(jsonStr)
        return isCarryOver(parsed) ? parsed : null
      } catch {
        return null
      }
    })
    .filter((item): item is CarryOver => item !== null)
}

/**
 * Safely parses a string to float, returning 0 for invalid inputs.
 * Handles empty strings, null, undefined, and non-numeric strings gracefully.
 */
function safeParseFloat(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') {
    return 0
  }
  const parsed = parseFloat(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

/**
 * Transforms GraphQL AllowanceStat data to the format expected by child components.
 * This bridges the gap between GraphQL camelCase responses and the snake_case
 * format used by the legacy MobX models.
 */
export function transformAllowanceStat(graphqlData: AllowanceStatFragment): ParsedAllowanceStat {
  // Parse cycles once and get both Cycle objects and raw data
  const { cycles, rawCycles } = parseCyclesWithRaw(graphqlData.cycles)

  const attributes: AllowanceStatAttributes = {
    id: graphqlData.id,
    allowance_id: graphqlData.allowanceId,
    available_days: safeParseFloat(graphqlData.availableDays),
    used_days: safeParseFloat(graphqlData.usedDays),
    prorated_allowance_days: safeParseFloat(graphqlData.proratedAllowanceDays),
    incidences: safeParseFloat(graphqlData.incidences),
    accumulated_carry_over: safeParseFloat(graphqlData.accumulatedCarryOver),
    used_carry_over: safeParseFloat(graphqlData.usedCarryOver),
    carry_overs: parseCarryOvers(graphqlData.carryOvers),
    policy_allowance: safeParseFloat(graphqlData.policyAllowance),
    total_accrued_units: safeParseFloat(graphqlData.totalAccruedUnits),
    total_in_decimal: graphqlData.totalInDecimal
      ? safeParseFloat(graphqlData.totalInDecimal)
      : null,
    max_balance_cap: graphqlData.maxBalanceCap ? safeParseFloat(graphqlData.maxBalanceCap) : null,
    outstanding_units: safeParseFloat(graphqlData.outstandingUnits),
    used_units_until_reference_date: safeParseFloat(graphqlData.usedUnitsUntilReferenceDate),
    year: graphqlData.year,
    cycles: rawCycles,
    effective_accrued: graphqlData.effectiveAccrued
      ? safeParseFloat(graphqlData.effectiveAccrued)
      : null,
    advance: graphqlData.advance ? safeParseFloat(graphqlData.advance) : null,
    taken_from_carry_over: graphqlData.takenFromCarryOver
      ? safeParseFloat(graphqlData.takenFromCarryOver)
      : null,
    taken_from_incidences: graphqlData.takenFromIncidences
      ? safeParseFloat(graphqlData.takenFromIncidences)
      : null,
    taken_from_accrued: graphqlData.takenFromAccrued
      ? safeParseFloat(graphqlData.takenFromAccrued)
      : null,
    taken_as_advance: graphqlData.takenAsAdvance
      ? safeParseFloat(graphqlData.takenAsAdvance)
      : null,
    carry_over_remaining: graphqlData.carryOverRemaining
      ? safeParseFloat(graphqlData.carryOverRemaining)
      : null,
    incidences_remaining: graphqlData.incidencesRemaining
      ? safeParseFloat(graphqlData.incidencesRemaining)
      : null,
    accrued_remaining: graphqlData.accruedRemaining
      ? safeParseFloat(graphqlData.accruedRemaining)
      : null,
    total: safeParseFloat(graphqlData.total),
  }

  const cycleIndex = (cycle: Cycle): number => {
    return cycles.findIndex((item) => item.id === cycle.id)
  }

  return {
    attributes,
    cycles,
    get firstCycle() {
      return cycles[0]
    },
    get lastCycle() {
      return cycles[cycles.length - 1]
    },
    cycleByDate(date: moment.Moment): Cycle | undefined {
      return cycles.find((cycle) => cycle.covers(date))
    },
    nextCycle(cycle: Cycle): Cycle | undefined {
      const index = cycleIndex(cycle)

      if (index < 0) {
        return undefined
      }

      return cycles[index + 1]
    },
    prevCycle(cycle: Cycle): Cycle | undefined {
      const index = cycleIndex(cycle)

      if (index < 0) {
        return undefined
      }

      return cycles[index - 1]
    },
    toJS() {
      return attributes
    },
  }
}
